import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import * as api from "../../../redux/api";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { reactIcons } from "../../../utils/icons";
import CustomFileInput from "../../CustomFileInput";

const BeneficialOwnership = () => {
  const [beneficialOwners, setBeneficialOwners] = useState([
    {
      fullName: "",
      nationality: "",
      ownershipPercent: "",
      isPEP: false,
      pepFullName: "",
      pepPosition: "",
      pepRelationship: "",
      documentFilePath: []
    }
  ]);
  const [formError, setFormError] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isReadOnly, setIsReadOnly] = useState(false);

  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const { key } = location.state || {};

  const getData = async () => {
    try {
      if (!id) return;
      const res = await api.getUserList(id);
      if (res.status && res.data) {
        const existingOwners = res?.data?.data?.BeneficialOwner || [];

        if (existingOwners.length > 0) {
          setIsReadOnly(true);
          const formattedData = existingOwners.map((owner) => ({
            fullName: owner.fullName || "",
            nationality: owner.nationality || "",
            ownershipPercent: owner.ownershipPercent || "",
            isPEP: owner.isPEP || false,
            pepFullName: owner.pepFullName || "",
            pepPosition: owner.pepPosition || "",
            pepRelationship: owner.pepRelationship || "",
            documentFilePath: (owner.documentFilePath || []).map((url) => ({
              name: url.split("/").pop(),
              url
            }))
          }));
          setBeneficialOwners(formattedData);
        }
      } else {
        console.error("Error fetching data:", res.message);
      }
    } catch (error) {
      console.error("API Error:", error);
    }
  };

  useEffect(() => {
    if (id) {
      getData();
    }
  }, [id]);

  const handleOwnerChange = (index, e) => {
    const { name, value } = e.target;
    setFormError((prevErrors) => ({
      ...prevErrors,
      [`${name}_${index}`]: ""
    }));

    const updatedOwners = beneficialOwners.map((owner, i) =>
      i === index ? { ...owner, [name]: value } : owner
    );
    setBeneficialOwners(updatedOwners);
  };

  const handlePepsChange = (index, e) => {
    const isYes = e.target.value === "yes";
    const updatedOwners = beneficialOwners.map((owner, i) =>
      i === index
        ? {
            ...owner,
            isPEP: isYes,
            pepFullName: "",
            pepPosition: "",
            pepRelationship: ""
          }
        : owner
    );
    setBeneficialOwners(updatedOwners);
  };

  const handleFileInputChange = (index, urls) => {
    const formatted = urls.map((url) => ({
      name: url.split("/").pop(),
      url
    }));
    setBeneficialOwners((prev) =>
      prev.map((owner, i) =>
        i === index ? { ...owner, documentFilePath: formatted } : owner
      )
    );

    setFormError((prev) => ({
      ...prev,
      [`documentFilePath_${index}`]: ""
    }));
  };

  const removeOwner = (index) => {
    if (beneficialOwners.length === 1) return;
    const updatedOwners = beneficialOwners.filter((_, i) => i !== index);
    setBeneficialOwners(updatedOwners);
  };

  const addNewBeneficialOwner = () => {
    setBeneficialOwners([
      ...beneficialOwners,
      {
        fullName: "",
        nationality: "",
        ownershipPercent: "",
        isPEP: false,
        pepFullName: "",
        pepPosition: "",
        pepRelationship: "",
        documentFilePath: []
      }
    ]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isLoading) return;

    let errors = {};
    beneficialOwners.forEach((owner, index) => {
      if (!owner.fullName)
        errors[`fullName_${index}`] = "Full Name is required";
      if (!owner.nationality)
        errors[`nationality_${index}`] = "Nationality is required";
      if (!owner.ownershipPercent && owner.ownershipPercent !== 0) {
        errors[`ownershipPercent_${index}`] =
          "Ownership Percentage is required";
      } else if (owner.ownershipPercent <= 0 || owner.ownershipPercent > 100) {
        errors[`ownershipPercent_${index}`] =
          "Ownership Percentage must be between 1 and 100";
      }
      if (owner.isPEP) {
        if (!owner.pepFullName)
          errors[`pepFullName_${index}`] = "PEP Full Name is required";
        if (!owner.pepPosition)
          errors[`pepPosition_${index}`] = "PEP Position is required";
        if (!owner.pepRelationship)
          errors[`pepRelationship_${index}`] = "PEP Relationship is required";
        if (!owner.documentFilePath.length)
          errors[`documentFilePath_${index}`] = "Upload a PEP document";
      }
    });

    setFormError(errors);
    if (Object.keys(errors).length > 0) return;

    try {
      setIsLoading(true);
      const payload = {
        beneficialOwners: beneficialOwners.map((owner) => {
          const {
            fullName,
            nationality,
            ownershipPercent,
            isPEP,
            pepFullName,
            pepPosition,
            pepRelationship,
            documentFilePath
          } = owner;
          const ownerObj = {
            organizationId: id,
            fullName,
            nationality,
            ownershipPercent,
            isPEP
          };
          if (isPEP) {
            ownerObj.pepFullName = pepFullName;
            ownerObj.pepPosition = pepPosition;
            ownerObj.pepRelationship = pepRelationship;
            ownerObj.documentFilePath = documentFilePath.map((f) => f.url);
          }
          return ownerObj;
        })
      };

      const res = await api.beneficialOwner(payload);
      if (res.status === 200 || res.status === 201) {
        toast.success("Data added successfully");
        navigate(`/risk-assesment/${id}`, { state: { key } });
      } else {
        toast.error(res?.error?.message);
      }
    } catch (error) {
      toast.error(
        error.response?.data?.message ||
          "An error occurred while submitting the form"
      );
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div>
      <h2 className="text-black text-24 text-center">Beneficial Ownership</h2>
      <form
        className="border mt-5 border-yellow-dark p-5 rounded-[10px]"
        onSubmit={(e) => {
          e.preventDefault();
          if (key) {
            handleSubmit(e);
          } else if (id) {
            navigate(`/risk-assesment/${id}`);
          } else {
            handleSubmit(e);
          }
        }}
      >
        {beneficialOwners.map((owner, index) => (
          <div key={index} className="mb-8 border-b pb-4">
            {beneficialOwners.length > 1 && (
              <h3 className="mb-4 text-lg font-bold">
                Beneficial Owner {index + 1}
              </h3>
            )}
            <div className="grid grid-cols-2 gap-4 items-center">
              <div className="space-y-2">
                <label className="text-black mb-2">
                  Full Name<span className="text-red-600">*</span>
                </label>
                <input
                  type="text"
                  name="fullName"
                  value={owner.fullName}
                  onChange={(e) => handleOwnerChange(index, e)}
                  placeholder="Enter full name"
                  readOnly={isReadOnly}
                  className={`text-input ${isReadOnly ? "readonly-input" : ""}`}
                />
                {formError[`fullName_${index}`] && (
                  <div className="text-red-500">
                    {formError[`fullName_${index}`]}
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <label className="text-black">
                  Nationality<span className="text-red-600">*</span>
                </label>
                <input
                  type="text"
                  name="nationality"
                  value={owner.nationality}
                  onChange={(e) => handleOwnerChange(index, e)}
                  placeholder="Enter nationality"
                  readOnly={isReadOnly}
                  className={`text-input ${isReadOnly ? "readonly-input" : ""}`}
                />
                {formError[`nationality_${index}`] && (
                  <div className="text-red-500">
                    {formError[`nationality_${index}`]}
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <label className="text-black">
                  Ownership Percentage<span className="text-red-600">*</span>
                </label>
                <input
                  type="number"
                  name="ownershipPercent"
                  value={owner.ownershipPercent}
                  onChange={(e) => handleOwnerChange(index, e)}
                  placeholder="Enter ownership percentage"
                  readOnly={isReadOnly}
                  className={`text-input ${isReadOnly ? "readonly-input" : ""}`}
                />
                {formError[`ownershipPercent_${index}`] && (
                  <div className="text-red-500">
                    {formError[`ownershipPercent_${index}`]}
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <label className="text-black">
                  Owner PEP (Politically Exposed Person)
                </label>
                <select
                  name="isPEP"
                  value={owner.isPEP ? "yes" : "no"}
                  onChange={(e) => handlePepsChange(index, e)}
                  disabled={isReadOnly}
                  className={`text-input ${isReadOnly ? "readonly-input" : ""}`}
                >
                  <option value="no">No</option>
                  <option value="yes">Yes</option>
                </select>
              </div>

              {owner.isPEP && (
                <div className="grid grid-cols-2 gap-4 mt-4 col-span-2">
                  <div className="space-y-2">
                    <label className="text-black">
                      PEP Full Name<span className="text-red-600">*</span>
                    </label>
                    <input
                      type="text"
                      name="pepFullName"
                      value={owner.pepFullName}
                      onChange={(e) => handleOwnerChange(index, e)}
                      placeholder="PEP Full Name"
                      readOnly={isReadOnly}
                      className={`text-input ${
                        isReadOnly ? "readonly-input" : ""
                      }`}
                    />
                    {formError[`pepFullName_${index}`] && (
                      <div className="text-red-500">
                        {formError[`pepFullName_${index}`]}
                      </div>
                    )}
                  </div>
                  <div className="space-y-2">
                    <label className="text-black">
                      PEP Position<span className="text-red-600">*</span>
                    </label>
                    <input
                      type="text"
                      name="pepPosition"
                      value={owner.pepPosition}
                      onChange={(e) => handleOwnerChange(index, e)}
                      placeholder="PEP Position/Role"
                      readOnly={isReadOnly}
                      className={`text-input ${
                        isReadOnly ? "readonly-input" : ""
                      }`}
                    />
                    {formError[`pepPosition_${index}`] && (
                      <div className="text-red-500">
                        {formError[`pepPosition_${index}`]}
                      </div>
                    )}
                  </div>
                  <div className="space-y-2">
                    <label className="text-black">
                      PEP Relationship<span className="text-red-600">*</span>
                    </label>
                    <input
                      type="text"
                      name="pepRelationship"
                      value={owner.pepRelationship}
                      onChange={(e) => handleOwnerChange(index, e)}
                      placeholder="PEP Relationship to Entity"
                      readOnly={isReadOnly}
                      className={`text-input ${
                        isReadOnly ? "readonly-input" : ""
                      }`}
                    />
                    {formError[`pepRelationship_${index}`] && (
                      <div className="text-red-500">
                        {formError[`pepRelationship_${index}`]}
                      </div>
                    )}
                  </div>

                  <div>
                    <CustomFileInput
                      label="Upload Document"
                      allowedFormats=" PDF"
                      name="documentFilePath"
                      defaultValue={owner.documentFilePath.map((f) => f.url)}
                      onChange={(urls) => handleFileInputChange(index, urls)}
                      disabled={isReadOnly}
                    />
                    {formError[`documentFilePath_${index}`] && (
                      <div className="text-red-500 mt-1 text-sm">
                        {formError[`documentFilePath_${index}`]}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
            {!isReadOnly && beneficialOwners.length > 1 && (
              <div className="mt-2 text-right">
                <button
                  type="button"
                  onClick={() => removeOwner(index)}
                  className="btn btn-danger"
                >
                  Remove
                </button>
              </div>
            )}
          </div>
        ))}

        {!isReadOnly && (
          <div className="mb-4">
            <button
              type="button"
              onClick={addNewBeneficialOwner}
              className="btn btn-primary"
            >
              {reactIcons.addUser}Add Another Beneficial Owner
            </button>
          </div>
        )}

        <div className="flex justify-between items-center">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="btn btn-danger"
          >
            {reactIcons.arrowleft} Back
          </button>
          <button
            type="submit"
            disabled={isLoading}
            className="py-3 px-6 rounded-lg btn-primary mt-4"
          >
            {isLoading ? "Loading..." : "Next"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default BeneficialOwnership;
